import type {
  SupportGroup,
  SupportGroupKey,
  SupportReferences,
  SupportRequestPayload,
  SupportRequestResponse,
  SupportTicket,
} from '@/types/Support';
import type { User } from '@/types/User';
import { buildClientContext } from '@/utils/clientContext';
import { getApiErrorMessage } from '@/utils/getApiErrorMessage';
import { getApiFieldErrors } from '@/utils/getApiFieldErrors';
import { helpers, requiredIf } from '@vuelidate/validators';
import type { ComputedRef } from 'vue';

export const SUPPORT_MESSAGE_MAX_LENGTH = 10000;

type SupportFormData = {
  reason: string;
  message: string;
  name: string;
  email: string;
  phone: string;
  order_id: string;
};

/** Maps API validation keys back onto form fields. */
const SERVER_ERROR_FIELDS: Record<string, keyof SupportFormData> = {
  reason: 'reason',
  message: 'message',
  name: 'name',
  email: 'email',
  phone: 'phone',
  'references.order_id': 'order_id',
};

/**
 * Form state and submission for `POST /support/requests`.
 *
 * Auth is optional: a bearer token is attached by `$api` when the visitor is
 * signed in, and only then may `references.order_id` be sent — the API rejects
 * an order reference from a guest. For guests the order number is folded into
 * the message instead so the detail is never lost.
 */
export function useSupportRequest({
  groupKey,
  group,
}: {
  groupKey: SupportGroupKey;
  group: ComputedRef<SupportGroup | undefined>;
}) {
  const { validations } = useValidation();
  const { showSnackbar } = useSnackbar();
  const { data: session, status } = useAuth();
  const route = useRoute();

  const isAuthenticated = computed(() => status.value === 'authenticated');
  const account = computed(
    () => (session.value as { data?: Partial<User> } | null)?.data
  );
  const hasAccountName = computed(
    () => isAuthenticated.value && Boolean(account.value?.full_name)
  );
  const hasAccountEmail = computed(
    () => isAuthenticated.value && Boolean(account.value?.email)
  );

  const { form, v$, getErrors, handleSubmit } = useForm<SupportFormData>({
    form: {
      reason: String(route.query.reason ?? ''),
      message: '',
      name: '',
      email: '',
      phone: '',
      order_id: String(route.query.order ?? ''),
    },
    rules: {
      reason: {
        required: validations.required('Reason'),
      },
      message: {
        required: validations.required('Message'),
        maxLength: validations.maxLength('Message', SUPPORT_MESSAGE_MAX_LENGTH),
      },
      name: {
        required: helpers.withMessage(
          () => 'Name is required',
          requiredIf(() => !hasAccountName.value)
        ),
        maxLength: validations.maxLength('Name', 255),
      },
      email: {
        required: helpers.withMessage(
          () => 'Email is required',
          requiredIf(() => !hasAccountEmail.value)
        ),
        email: validations.email(),
      },
      phone: {
        maxLength: validations.maxLength('Phone', 50),
      },
      order_id: {
        maxLength: validations.maxLength('Order number', 50),
      },
    },
  });

  /** Contact details are only asked of guests — the account supplies them otherwise. */
  const needsContactDetails = computed(
    () => !hasAccountName.value || !hasAccountEmail.value
  );

  const selectedReason = computed(() =>
    group.value?.reasons.find(({ value }) => value === form.value.reason)
  );

  /** Whether the chosen reason is tied to an order, per the API's `requires`. */
  const requiresOrder = computed(() =>
    Boolean(selectedReason.value?.requires.includes('order_id'))
  );

  const serverErrors = ref<Partial<Record<keyof SupportFormData, string>>>({});
  const errors = computed(() => getErrors(serverErrors.value));

  const ticket = ref<SupportTicket>();
  const { pending, data, error, execute } = useSubmit<SupportRequestResponse>();

  const buildPayload = (): SupportRequestPayload => {
    const orderNumber = form.value.order_id.trim();
    const includeReference =
      requiresOrder.value && Boolean(orderNumber) && isAuthenticated.value;
    const references: SupportReferences = includeReference
      ? { order_id: orderNumber }
      : {};

    // A guest cannot send `references.order_id`, so keep the number in the message body.
    const message =
      requiresOrder.value && orderNumber && !includeReference
        ? `${form.value.message.trim()}\n\nOrder number: ${orderNumber}`
        : form.value.message.trim();

    const clientContext = group.value?.client_context_required
      ? buildClientContext(
          String(route.query.screen ?? route.fullPath),
          group.value.client_context_fields
        )
      : {};

    return {
      support_group: groupKey,
      reason: form.value.reason,
      message,
      source: 'web',
      ...(form.value.name.trim() ? { name: form.value.name.trim() } : {}),
      ...(form.value.email.trim() ? { email: form.value.email.trim() } : {}),
      ...(form.value.phone.trim() ? { phone: form.value.phone.trim() } : {}),
      ...(Object.keys(references).length ? { references } : {}),
      ...(Object.keys(clientContext).length
        ? { client_context: clientContext }
        : {}),
    };
  };

  const submit = async () => {
    await handleSubmit(async () => {
      if (pending.value) return;

      serverErrors.value = {};

      await execute('support/requests', {
        method: 'POST',
        body: buildPayload(),
        showSnackbar: false,
      });

      if (error.value) {
        const fieldErrors = getApiFieldErrors(error.value);
        serverErrors.value = Object.entries(fieldErrors).reduce<
          Partial<Record<keyof SupportFormData, string>>
        >((accumulator, [key, message]) => {
          const field = SERVER_ERROR_FIELDS[key];
          if (field) accumulator[field] = message;
          return accumulator;
        }, {});

        showSnackbar({
          text:
            getApiErrorMessage(error.value) ||
            'We could not send your request. Please try again.',
          state: false,
        });
        return;
      }

      ticket.value = data.value?.data;
    });
  };

  const reset = () => {
    ticket.value = undefined;
    serverErrors.value = {};
    form.value = {
      reason: '',
      message: '',
      name: '',
      email: '',
      phone: '',
      order_id: '',
    };
    v$.value.$reset();
  };

  return {
    form,
    v$,
    errors,
    pending,
    ticket,
    isAuthenticated,
    needsContactDetails,
    requiresOrder,
    selectedReason,
    submit,
    reset,
  };
}
